import { createFileRoute } from "@tanstack/react-router";
import SimpleTable, { TableExports } from "@/components/ui/table";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { useEffect } from "react";
import FullPagination from "@/components/ui/shared/full-pagination";
import {
  OrderStatusOptions,
  OrderDeliveryStatusOptions,
  OrderTableColumns,
} from "@/components/order/order-meta";
import { SearchEngine } from "@/components/ui/shared/search-engine";
import { Package, Plus } from "lucide-react";
import type { Order } from "@/store/Order";
import type { Shop } from "@/store/Shop";
import { Toast } from "@/components/ui/toast";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import { useTableAttributes } from "@/hooks/use-table-attributes";
import { orderCSV, orderPDF } from "@/components/order/order-export";
import { TableAttributesModal } from "@/components/ui/table-attributes-modal";
import OrderStats from "@/components/order/order-stats";

export const Route = createFileRoute("/(dasboard)/orders/orders/")({
  component: OrdersPage,
});

function OrdersPage() {
  const store = useAppStore();
  const currentShop = store.auth.session?.shop as Shop;
  const navigate = useNavigate();
  const { t } = useTranslate();
  const tableAttributes = useTableAttributes();
  const tableName = "orders";

  const searchForm = useSimpleForm({
    keyword: undefined,
    status: undefined,
    user_id: undefined,
    start_date: undefined,
    end_date: undefined,
  });

  const handleAutocomplete = (values: any) => {
    store.order.paginate(
      store.order.items.filter((order: Order) => {
        let matchKeyword = true;

        if (values.keyword) {
          matchKeyword =
            order.id.toLowerCase().includes(values.keyword.toLowerCase()) ||
            order.customer?.first_name
              ?.toLowerCase()
              .includes(values.keyword.toLowerCase()) ||
            order.customer?.last_name
              ?.toLowerCase()
              .includes(values.keyword.toLowerCase());
        }

        const matchStatus = values.status
          ? order.status === values.status
          : true;

        const matchDeliveryStatus = values.delivery_status
          ? order.delivery_status === values.delivery_status
          : true;

        return matchKeyword && matchStatus && matchDeliveryStatus;
      })
    );
  };

  const search = ({ page = 1, per_page = 10 }: any = {}) => {
    store.order.fetch({
      shop_id: currentShop.id,
      page,
      per_page,
      ...searchForm.values,
    });
  };

  useEffect(() => {
    if (currentShop)
      search({
        shop_id: currentShop?.id,
      });

    tableAttributes.loadAttributes(tableName, [
      "id",
      "customer",
      "status",
      "delivery_status",
      "net_amount",
      "created_at",
      "actions",
    ]);
  }, [store.auth.session, currentShop]);

  const columns = OrderTableColumns({
    store,
    onView: (order: any) => {
      navigate({ to: `/orders/orders/${order.id}` });
    },
  }).filter((item) =>
    (tableAttributes.attributes[tableName] ?? []).includes(item.name)
  );

  return (
    <>
      <TableAttributesModal
        tableName={tableName}
        columns={OrderTableColumns({ store })}
      />
      <Toast name="clipboard" message="Copy to clipboard" autoClose={2000} />

      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
          <h2 className="text-2xl font-semibold">{t("Orders")}</h2>
          <Button
            onClick={() => {
              store.display.show("create_order_block");
              navigate({ to: "/orders/orders/create" });
            }}
          >
            <Plus className="w-4 h-4" />
            {t("Add order")}
          </Button>
        </header>

        <OrderStats />

        <div className="mt-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="w-full lg:w-auto">
            <SearchEngine
              autoComplete={handleAutocomplete}
              selects={[
                {
                  placeholder: "All statuses",
                  options: OrderStatusOptions,
                  name: "status",
                  icon: <Package className="w-4 h-4" />,
                },
                {
                  placeholder: "All delivery statuses",
                  options: OrderDeliveryStatusOptions,
                  name: "delivery_status",
                  size: "w-full sm:w-[250px]",
                  icon: <Package className="w-4 h-4" />,
                },
              ]}
              form={searchForm}
              onSubmit={undefined}
            />
          </div>
          <div className="flex items-center gap-2 mb-4 lg:mb-0 self-end lg:self-auto">
            <TableExports
              pdf={() =>
                orderPDF(columns, store.order.pagination.data, t, store)
              }
              csv={() => orderCSV(columns, store.order.pagination.data, store)}
            />
          </div>
        </div>

        <SimpleTable items={store.order.pagination.data} columns={columns} />

        <FullPagination
          className="mt-4"
          canEdit={true}
          pagination={store.order.pagination}
          onGoto={(pageNumber: number) => search({ page: pageNumber })}
          onPerPage={(per_page) => search({ per_page: per_page })}
        />
      </div>
    </>
  );
}
